import { useEffect, useRef, useState } from "react";
import { UploadCloud, X, Film, Image as ImageIcon, CheckCircle2, AlertCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { accentFor } from "../../lib/accents";
import { useConfirm } from "../../components/ConfirmDialog";

export default function AdminUpload() {
  const confirm = useConfirm();
  const [categories, setCategories] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [autoTag, setAutoTag] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data));
  }, []);

  const runDupCheck = async (file, index) => {
    // Fires per file after it lands in the queue — updates the item with duplicate info
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/ads/check-duplicate", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((prev) => prev.map((x, i) => (i === index ? { ...x, dup_checked: true, duplicate: data.duplicate, existing: data.existing || null, allow_duplicate: false } : x)));
    } catch (_err) {
      setItems((prev) => prev.map((x, i) => (i === index ? { ...x, dup_checked: true, dup_check_failed: true } : x)));
    }
  };

  const addFiles = (fileList) => {
    const valid = Array.from(fileList).filter((f) => f.type.startsWith("image/") || f.type.startsWith("video/"));
    if (valid.length < fileList.length) toast.error("Some files were skipped (only images & videos allowed)");
    setItems((prev) => {
      const startIndex = prev.length;
      const next = [
        ...prev,
        ...valid.map((f) => ({
          file: f,
          title: f.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " "),
          hook: "",
          angle: "",
          offer: "",
          status: "pending",
          dup_checked: false,
          duplicate: false,
        })),
      ];
      // Kick off duplicate checks in the background
      valid.forEach((f, i) => runDupCheck(f, startIndex + i));
      return next;
    });
  };

  const CHUNK_THRESHOLD = 8 * 1024 * 1024; // 8MB — smaller files skip chunking entirely (fast path)
  const CHUNK_SIZE = 4 * 1024 * 1024;      // 4MB chunks — big enough for throughput, small enough for proxy limits
  const CHUNK_RETRIES = 6;                 // retry each chunk up to 6 times before giving up

  // SHA-256 of an ArrayBuffer using the Web Crypto API. Used per-chunk only.
  const sha256Bytes = async (buf) => {
    if (!crypto?.subtle) return null;
    try {
      const hashBuf = await crypto.subtle.digest("SHA-256", buf);
      return Array.from(new Uint8Array(hashBuf)).map((b) => b.toString(16).padStart(2, "0")).join("");
    } catch { return null; }
  };

  const postChunkWithRetry = async (uploadId, chunkBuf, chunkIdx, offset, chunkHash) => {
    let lastErr;
    for (let attempt = 0; attempt < CHUNK_RETRIES; attempt++) {
      try {
        return await api.post("/admin/ads/upload-chunk", chunkBuf, {
          headers: {
            "Content-Type": "application/octet-stream",
            "X-Upload-Id": uploadId,
            "X-Chunk-Offset": String(offset),
            ...(chunkHash ? { "X-Chunk-Sha256": chunkHash } : {}),
          },
          timeout: 120000,
        });
      } catch (err) {
        lastErr = err;
        // 422 = server saw byte corruption on THIS chunk — retry aggressively
        const isCorruption = err?.response?.status === 422;
        const wait = isCorruption ? 300 : 500 * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, wait));
      }
    }
    const msg = lastErr?.response?.data?.detail || lastErr?.message || "network error";
    throw new Error(`Chunk ${chunkIdx + 1} failed after ${CHUNK_RETRIES} tries: ${msg}`);
  };

  const uploadChunked = async (file, formFields, onProgress) => {
    // Sequential per-chunk upload with per-chunk hash verification. Each chunk carries
    // its own SHA-256; if the server sees corrupted bytes it rejects with 422 and the
    // client retries just that chunk. Per-chunk verification is sufficient — no need
    // for a whole-file hash (which was unreliable on 100MB+ files because
    // file.arrayBuffer() can return partial buffers under browser memory pressure).
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const total = file.size;
    let chunkIdx = 0;
    for (let start = 0; start < total; start += CHUNK_SIZE) {
      const end = Math.min(start + CHUNK_SIZE, total);
      const buf = await file.slice(start, end).arrayBuffer();
      const chunkHash = await sha256Bytes(buf);
      await postChunkWithRetry(uploadId, buf, chunkIdx, start, chunkHash);
      chunkIdx += 1;
      if (onProgress) onProgress(Math.round((end / total) * 100));
    }
    // Server-side size sanity check — confirms every chunk landed
    try {
      const { data: statusData } = await api.post("/admin/ads/upload-status", null, {
        headers: { "X-Upload-Id": uploadId },
      });
      if (statusData?.received_bytes !== total) {
        throw new Error(`Upload incomplete: server has ${statusData?.received_bytes ?? 0} of ${total} bytes. Please retry.`);
      }
    } catch (err) {
      if (err.response?.status === 404) throw new Error("Upload session expired. Please retry.");
      throw err;
    }
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("filename", file.name);
    fd.append("content_type", file.type || "application/octet-stream");
    fd.append("total_size", String(total));
    Object.entries(formFields).forEach(([k, v]) => fd.append(k, v));
    const { data } = await api.post("/admin/ads/upload-finalize", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 300000,
    });
    return data;
  };

  const upload = async () => {
    setUploading(true);
    const next = [...items];
    for (let i = 0; i < next.length; i++) {
      if (next[i].status === "done") continue;
      // Skip duplicates unless the admin explicitly opted in
      if (next[i].duplicate && !next[i].allow_duplicate) {
        next[i] = { ...next[i], status: "skipped" };
        setItems([...next]);
        continue;
      }
      next[i] = { ...next[i], status: "uploading", progress: 0 };
      setItems([...next]);
      const fields = {
        title: next[i].title || next[i].file.name,
        description: "",
        hook: next[i].hook || "",
        angle: next[i].angle || "",
        offer: next[i].offer || "",
        category_ids: selectedCats.join(","),
        auto_tag: autoTag ? "true" : "false",
        allow_duplicate: next[i].allow_duplicate ? "true" : "false",
      };
      try {
        let data;
        if (next[i].file.size > CHUNK_THRESHOLD) {
          data = await uploadChunked(next[i].file, fields, (pct) => {
            setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, progress: pct } : x)));
          });
        } else {
          const fd = new FormData();
          fd.append("file", next[i].file);
          Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
          const res = await api.post("/admin/ads", fd, { headers: { "Content-Type": "multipart/form-data" } });
          data = res.data;
        }
        next[i] = { ...next[i], status: "done", processing: data.status === "processing", progress: 100 };
      } catch (e) {
        next[i] = { ...next[i], status: "error", error: apiError(e) };
      }
      setItems([...next]);
    }
    setUploading(false);
    const done = next.filter((x) => x.status === "done").length;
    const skipped = next.filter((x) => x.status === "skipped").length;
    if (done) toast.success(`${done} ad${done > 1 ? "s" : ""} uploaded. Videos keep processing in the background.`);
    if (skipped) toast.info(`${skipped} duplicate${skipped > 1 ? "s" : ""} skipped.`);
  };

  const toggleCat = (id) =>
    setSelectedCats((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]));

  return (
    <div className="fade-up max-w-3xl">
      <h1 className="font-display font-black text-3xl text-[#000000]">Upload Ads</h1>
      <p className="text-gray-500 mt-1 text-sm">
        Drop images or videos — each is auto-sized into Feed (1:1) and Stories/Reels (9:16) using safe-fit, so nothing gets cropped out. No batch limit; images up to 200MB, videos up to 2GB each.
      </p>

      <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-4 flex items-start gap-4" data-testid="auto-tag-panel">
        <div className="w-9 h-9 rounded-full bg-[#DF1283] text-white flex items-center justify-center shrink-0 font-display font-black text-sm">AI</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-display font-black text-sm text-[#000000]">Auto-tag with AI</div>
              <div className="text-xs text-gray-500 mt-0.5">Gemini reads each creative, fills Hook / Angle / Offer, and picks a category — creating a new one (with a brand color) if none fits.</div>
            </div>
            <button
              type="button"
              data-testid="auto-tag-toggle"
              onClick={() => setAutoTag((v) => !v)}
              role="switch"
              aria-checked={autoTag}
              className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-200 ${autoTag ? "bg-[#000000]" : "bg-gray-200"}`}
            >
              <span
                className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200"
                style={{ transform: autoTag ? "translateX(20px)" : "translateX(0)" }}
              />
            </button>
          </div>
        </div>
      </div>

      <div
        data-testid="upload-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="mt-4 border-2 border-dashed border-gray-300 rounded-2xl p-12 text-center cursor-pointer bg-white hover:border-[#0FFFE2] transition-colors duration-200"
      >
        <UploadCloud size={36} className="mx-auto text-gray-400" />
        <p className="font-display font-bold text-[#000000] mt-3">Click or drag files here</p>
        <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP, MP4, MOV — bulk upload supported</p>
        <input ref={inputRef} data-testid="upload-file-input" type="file" multiple accept="image/*,video/*"
          className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>

      <div className="mt-6">
        <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Assign categories (applies to this batch)</label>
        <div className="flex flex-wrap gap-2 mt-2">
          {categories.map((c) => {
            const active = selectedCats.includes(c.id);
            const a = accentFor(c.color);
            return (
              <span key={c.id} className="inline-flex items-center rounded-full text-xs font-bold transition-colors overflow-hidden"
                style={active ? { background: a.hex, color: a.text } : { background: "#FFFFFF", color: "#000000", border: "1px solid #E5E7EB" }}>
                <button type="button" data-testid={`upload-cat-chip-${c.slug}`} onClick={() => toggleCat(c.id)}
                  className="pl-3.5 pr-2 py-1.5">
                  {c.name}
                </button>
                <button type="button" data-testid={`upload-cat-delete-${c.slug}`}
                  onClick={async () => {
                    if (!(await confirm({
                      title: `Delete "${c.name}" category?`,
                      message: "This unassigns the category from every ad that uses it. The ads themselves are kept.",
                      tone: "danger",
                      confirmText: "Delete category",
                    }))) return;
                    try {
                      await api.delete(`/admin/categories/${c.id}`);
                      setCategories((prev) => prev.filter((x) => x.id !== c.id));
                      setSelectedCats((prev) => prev.filter((x) => x !== c.id));
                      toast.success(`Deleted "${c.name}"`);
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                  aria-label={`Delete ${c.name}`}
                  className="pr-2 pl-1 py-1.5 opacity-70 hover:opacity-100 hover:bg-black/10 transition-opacity">
                  <X size={12} strokeWidth={3} />
                </button>
              </span>
            );
          })}
          {categories.length === 0 && <span className="text-xs text-gray-400">No categories yet — create some in the Categories tab.</span>}
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="upload-queue">
          {items.map((item, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-3">
              <div className="flex items-center gap-3">
                {item.file.type.startsWith("video/") ? <Film size={18} className="text-[#4A0A66] shrink-0" /> : <ImageIcon size={18} className="text-[#0FFFE2] shrink-0" />}
                <input
                  data-testid={`upload-title-input-${i}`}
                  value={item.title}
                  disabled={item.status !== "pending"}
                  onChange={(e) => setItems((prev) => prev.map((x, xi) => (xi === i ? { ...x, title: e.target.value } : x)))}
                  className="flex-1 text-sm font-semibold text-[#000000] bg-transparent focus:outline-none border-b border-transparent focus:border-[#0FFFE2]"
                />
                <span className="text-[11px] text-gray-400 shrink-0">{(item.file.size / 1024 / 1024).toFixed(1)} MB</span>
                {item.status === "pending" && (
                  <button data-testid={`upload-remove-btn-${i}`} onClick={() => setItems((prev) => prev.filter((_, xi) => xi !== i))}
                    className="p-1 rounded-full hover:bg-gray-100 text-gray-400"><X size={14} /></button>
                )}
                {item.status === "uploading" && (
                  <div className="flex items-center gap-2 shrink-0">
                    {typeof item.progress === "number" && item.progress > 0 && item.progress < 100 && (
                      <span className="text-[10px] font-bold text-[#000000] tabular-nums" data-testid={`upload-progress-${i}`}>{item.progress}%</span>
                    )}
                    <div className="w-4 h-4 rounded-full border-2 border-gray-200 border-t-[#0FFFE2] spin-slow" />
                  </div>
                )}
                {item.status === "done" && <CheckCircle2 size={18} className="text-[#000000] shrink-0" />}
                {item.status === "skipped" && <span title="Skipped as duplicate"><AlertTriangle size={18} className="text-[#FC861E] shrink-0" /></span>}
                {item.status === "error" && <AlertCircle size={18} className="text-[#DF1283] shrink-0" />}
              </div>
              {item.status === "error" && (
                <div className="mt-2 ml-8 flex items-start gap-2 bg-[#DF1283] border border-[#DF1283] rounded-lg p-2.5" data-testid={`upload-error-${i}`}>
                  <AlertCircle size={14} className="text-[#DF1283] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="font-bold text-[#DF1283]">Upload failed</div>
                    <div className="text-gray-700 mt-0.5 break-words" data-testid={`upload-error-message-${i}`}>{item.error || "Unknown error — check the console or backend logs."}</div>
                  </div>
                  <button
                    data-testid={`upload-retry-btn-${i}`}
                    onClick={() => setItems((prev) => prev.map((x, xi) => (xi === i ? { ...x, status: "pending", error: null } : x)))}
                    className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#DF1283] hover:text-[#000000] transition-colors shrink-0"
                    title="Reset this file to pending so you can retry"
                  >
                    Retry
                  </button>
                </div>
              )}
              {item.status === "pending" && item.duplicate && item.existing && (
                <div className="mt-2 ml-8 flex items-start gap-2 bg-[#FC861E] border border-[#FC861E] rounded-lg p-2.5" data-testid={`duplicate-warning-${i}`}>
                  <AlertTriangle size={14} className="text-[#FC861E] mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="font-bold text-[#000000]">Already in your library</div>
                    <div className="text-gray-600 mt-0.5 truncate">
                      Matches: <span className="font-semibold text-[#000000]">&ldquo;{item.existing.title || "Untitled"}&rdquo;</span>
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] font-bold text-[#000000] cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      data-testid={`allow-duplicate-checkbox-${i}`}
                      checked={!!item.allow_duplicate}
                      onChange={(e) => setItems((prev) => prev.map((x, xi) => (xi === i ? { ...x, allow_duplicate: e.target.checked } : x)))}
                      className="accent-[#FC861E]"
                    />
                    Upload anyway
                  </label>
                </div>
              )}
              {item.status === "pending" && !item.dup_checked && (
                <div className="mt-1.5 ml-8 text-[11px] text-gray-400" data-testid={`dup-check-loading-${i}`}>Checking for duplicates…</div>
              )}
              {item.status === "pending" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2 pl-8">
                  {[
                    { key: "hook", ph: "Hook (e.g. 84% of CEOs have a mentor)" },
                    { key: "angle", ph: "Angle (e.g. authority / social proof)" },
                    { key: "offer", ph: "Offer (e.g. Free 7-day trial)" },
                  ].map((f) => (
                    <input
                      key={f.key}
                      data-testid={`upload-${f.key}-input-${i}`}
                      value={item[f.key]}
                      onChange={(e) => setItems((prev) => prev.map((x, xi) => (xi === i ? { ...x, [f.key]: e.target.value } : x)))}
                      placeholder={f.ph}
                      className="text-xs rounded-lg border border-gray-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
          <button
            data-testid="upload-submit-btn"
            onClick={upload}
            disabled={uploading || items.every((i) => i.status !== "pending")}
            className="w-full mt-2 bg-[#D0FF0D] text-[#000000] rounded-full px-6 py-3 font-bold text-sm hover:bg-[#0FFFE2] transition-colors duration-200 disabled:opacity-50"
          >
            {uploading ? "Uploading & processing..." : `Upload ${items.filter((i) => i.status === "pending").length} file(s)`}
          </button>
        </div>
      )}
    </div>
  );
}
